/* ============================================
   VENUM SHOPIFY THEME - Main JavaScript
   ============================================ */

'use strict';

// ---- Utility Functions ----
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);

function debounce(fn, delay = 300) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function formatMoney(cents) {
  const dollars = (cents / 100).toFixed(2);
  return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function showToast(msg, type = '') {
  let toast = $('.toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.className = 'toast' + (type ? ' toast--' + type : '');
  requestAnimationFrame(() => toast.classList.add('is-visible'));
  setTimeout(() => toast.classList.remove('is-visible'), 3200);
}

// ---- Lazy Load Images ----
function initLazyLoad() {
  if (!('IntersectionObserver' in window)) return;
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.onload = () => img.classList.add('is-loaded');
          obs.unobserve(img);
        }
      }
    });
  }, { rootMargin: '200px' });
  $$('img[data-src]').forEach(img => obs.observe(img));
}

// ---- Scroll Animations ----
function initFadeIn() {
  if (!('IntersectionObserver' in window)) {
    $$('.fade-in').forEach(el => el.classList.add('is-visible'));
    return;
  }
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });
  $$('.fade-in').forEach(el => obs.observe(el));
}

// ---- Announcement Bar ----
function initAnnouncementBar() {
  const bar = $('.announcement-bar');
  const closeBtn = $('.announcement-bar__close');
  if (!bar || !closeBtn) return;
  if (sessionStorage.getItem('announcement-closed')) {
    bar.style.display = 'none';
    return;
  }
  on(closeBtn, 'click', () => {
    bar.style.display = 'none';
    sessionStorage.setItem('announcement-closed', '1');
  });
}

// ---- Mobile Menu ----
function initMobileMenu() {
  const toggle = $('.mobile-menu-toggle');
  const menu = $('.mobile-menu');
  const overlay = $('.mobile-menu__overlay');
  if (!toggle || !menu) return;

  function openMenu() {
    menu.classList.add('is-open');
    overlay && overlay.classList.add('is-open');
    toggle.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }
  function closeMenu() {
    menu.classList.remove('is-open');
    overlay && overlay.classList.remove('is-open');
    toggle.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  on(toggle, 'click', () => menu.classList.contains('is-open') ? closeMenu() : openMenu());
  on(overlay, 'click', closeMenu);

  // Sub-menu toggles
  $$('.mobile-menu__link[data-has-sub]').forEach(link => {
    on(link, 'click', (e) => {
      e.preventDefault();
      const sub = link.nextElementSibling;
      if (sub && sub.classList.contains('mobile-sub')) {
        sub.classList.toggle('is-open');
        link.querySelector('svg') && link.querySelector('svg').classList.toggle('rotated');
      }
    });
  });

  // Close on escape
  on(document, 'keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
}

// ---- Search Overlay ----
function initSearch() {
  const toggleBtn = $('#search-toggle');
  const overlay = $('.search-overlay');
  const closeBtn = $('.search-overlay__close');
  const input = $('.search-overlay__input');
  if (!toggleBtn || !overlay) return;

  on(toggleBtn, 'click', () => {
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => input && input.focus(), 100);
  });
  function closeSearch() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  on(closeBtn, 'click', closeSearch);
  on(overlay, 'click', (e) => { if (e.target === overlay) closeSearch(); });
  on(document, 'keydown', (e) => { if (e.key === 'Escape') closeSearch(); });
}

// ---- Hero Slider (no-op — replaced by video hero) ----
function initHeroSlider() {
  // Hero is now a full-width video; no slider logic needed.
}

// ---- Product Carousel (auto-scroll infinite) ----
function initProductCarousels() {
  $$('.product-carousel').forEach(carousel => {
    const track = $('.product-carousel__track', carousel);
    if (!track) return;
    // Duplicate cards so the CSS infinite-scroll animation loops seamlessly
    const origCards = Array.from(track.children);
    if (origCards.length === 0) return;
    origCards.forEach(card => track.appendChild(card.cloneNode(true)));
    // Re-attach wishlist click handlers on cloned cards
    $$('.product-card__wishlist', track).forEach(btn => {
      const id = btn.dataset.productId;
      if (!id) return;
      if (Wishlist.has(id)) btn.classList.add('is-active');
      on(btn, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = Wishlist.toggle(id);
        btn.classList.toggle('is-active', added);
        showToast(added ? 'Added to wishlist' : 'Removed from wishlist');
      });
    });
  });
}

// ---- Cart Drawer ----
const CartDrawer = {
  drawer: null,
  overlay: null,
  content: null,

  init() {
    this.drawer = $('.cart-drawer');
    if (!this.drawer) return;
    this.overlay = $('.cart-drawer__overlay', this.drawer);
    this.content = $('#cart-drawer-content');
    const closeBtn = $('.cart-drawer__close', this.drawer);

    on(this.overlay, 'click', () => this.close());
    on(closeBtn, 'click', () => this.close());
    on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Cart icon trigger
    const cartTriggers = $$('[data-cart-trigger]');
    cartTriggers.forEach(el => on(el, 'click', (e) => {
      e.preventDefault();
      this.open();
    }));
  },

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  },

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.warn('Cart fetch error:', e);
    }
  },

  render(cart) {
    if (!this.content) return;
    const subtotalEl = $('.cart-drawer__subtotal');

    if (cart.item_count === 0) {
      this.content.innerHTML = `<div class="cart-drawer__empty">Your cart is empty.<br><a href="/collections/all" style="color:#000;text-decoration:underline;font-weight:600;">Continue Shopping</a></div>`;
      if (subtotalEl) subtotalEl.style.display = 'none';
      return;
    }

    if (subtotalEl) {
      subtotalEl.style.display = 'flex';
      const priceEl = subtotalEl.querySelector('.drawer-subtotal-price');
      if (priceEl) priceEl.textContent = formatMoney(cart.total_price);
    }

    this.content.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__img">
          <img src="${item.image || ''}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-item__info">
          <div class="cart-item__name">${item.product_title}</div>
          <div class="cart-item__variant">${item.variant_title || ''}</div>
          <div class="cart-item__qty">
            <button class="qty-btn" data-action="decrease" data-key="${item.key}">−</button>
            <input class="qty-input" type="number" value="${item.quantity}" min="0" data-key="${item.key}">
            <button class="qty-btn" data-action="increase" data-key="${item.key}">+</button>
          </div>
          <div class="cart-item__price">${formatMoney(item.line_price)}</div>
          <button class="cart-item__remove" data-key="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');

    // Bind events
    $$('.cart-item__remove', this.content).forEach(btn => {
      on(btn, 'click', () => this.updateItem(btn.dataset.key, 0));
    });
    $$('[data-action="decrease"]', this.content).forEach(btn => {
      on(btn, 'click', () => {
        const input = this.content.querySelector(`input[data-key="${btn.dataset.key}"]`);
        if (input) this.updateItem(btn.dataset.key, Math.max(0, parseInt(input.value) - 1));
      });
    });
    $$('[data-action="increase"]', this.content).forEach(btn => {
      on(btn, 'click', () => {
        const input = this.content.querySelector(`input[data-key="${btn.dataset.key}"]`);
        if (input) this.updateItem(btn.dataset.key, parseInt(input.value) + 1);
      });
    });
  },

  async updateItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this.render(cart);
      this.updateCartCount(cart.item_count);
    } catch (e) {
      console.warn('Cart update error:', e);
    }
  },

  updateCartCount(count) {
    $$('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
};

// ---- Add to Cart ----
function initAddToCart() {
  $$('form[action="/cart/add"]').forEach(form => {
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('.btn--add-cart, [type="submit"]');
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
      }
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.fromEntries(formData))
        });
        if (!res.ok) throw new Error('Failed to add item');
        showToast('Item added to cart!', 'success');
        CartDrawer.open();
        CartDrawer.fetchCart().then(cart => {
          if (cart) CartDrawer.updateCartCount(cart.item_count);
        });
      } catch (err) {
        showToast('Could not add item. Please try again.', 'error');
      } finally {
        if (btn) { btn.textContent = 'Add to Cart'; btn.disabled = false; }
      }
    });
  });

  // Quick add buttons on product cards
  $$('.product-card__quick-add').forEach(btn => {
    on(btn, 'click', async () => {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        showToast('Item added!', 'success');
        CartDrawer.open();
      } catch (e) { showToast('Error adding item.', 'error'); }
    });
  });
}

// ---- Product Page Gallery ----
function initProductGallery() {
  const gallery = $('.product-gallery');
  if (!gallery) return;

  // Thumbnail click
  $$('.product-gallery__thumb', gallery).forEach(thumb => {
    on(thumb, 'click', () => {
      $$('.product-gallery__thumb', gallery).forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const mainImg = $('.product-gallery__main img', gallery);
      if (mainImg && thumb.dataset.src) mainImg.src = thumb.dataset.src;
    });
  });

  // Grid gallery click (zoom)
  $$('.gallery-img', gallery).forEach((img, i) => {
    on(img, 'click', () => openLightbox(gallery, i));
  });
}

function openLightbox(gallery, startIndex) {
  const images = $$('.gallery-img img, .product-gallery__thumb img', gallery).map(img => img.src);
  if (!images.length) return;

  let current = startIndex;
  const lb = document.createElement('div');
  lb.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.92);z-index:2000;display:flex;align-items:center;justify-content:center;';

  const update = () => {
    img.src = images[current];
    counter.textContent = `${current + 1} / ${images.length}`;
  };

  const img = document.createElement('img');
  img.style.cssText = 'max-height:90vh;max-width:90vw;object-fit:contain;';

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '✕';
  closeBtn.style.cssText = 'position:absolute;top:20px;right:20px;background:rgba(255,255,255,0.2);border:none;color:white;width:40px;height:40px;border-radius:50%;font-size:18px;cursor:pointer;';

  const prevBtn = document.createElement('button');
  prevBtn.innerHTML = '‹';
  prevBtn.style.cssText = 'position:absolute;left:20px;background:rgba(255,255,255,0.2);border:none;color:white;width:48px;height:48px;font-size:28px;cursor:pointer;';

  const nextBtn = document.createElement('button');
  nextBtn.innerHTML = '›';
  nextBtn.style.cssText = 'position:absolute;right:20px;background:rgba(255,255,255,0.2);border:none;color:white;width:48px;height:48px;font-size:28px;cursor:pointer;';

  const counter = document.createElement('div');
  counter.style.cssText = 'position:absolute;bottom:20px;left:50%;transform:translateX(-50%);color:rgba(255,255,255,0.7);font-size:14px;';

  lb.append(img, closeBtn, prevBtn, nextBtn, counter);
  document.body.appendChild(lb);

  update();
  on(closeBtn, 'click', () => lb.remove());
  on(lb, 'click', (e) => { if (e.target === lb) lb.remove(); });
  on(prevBtn, 'click', () => { current = (current - 1 + images.length) % images.length; update(); });
  on(nextBtn, 'click', () => { current = (current + 1) % images.length; update(); });
  on(document, 'keydown', function handler(e) {
    if (e.key === 'Escape') { lb.remove(); off(document, 'keydown', handler); }
    if (e.key === 'ArrowLeft') { current = (current - 1 + images.length) % images.length; update(); }
    if (e.key === 'ArrowRight') { current = (current + 1) % images.length; update(); }
  });
}

// ---- Product Options (Color + Size) ----
function initProductOptions() {
  // Color swatches
  $$('.color-swatch--lg').forEach(swatch => {
    on(swatch, 'click', () => {
      const group = swatch.closest('.product-option__colors');
      if (group) $$('.color-swatch--lg', group).forEach(s => s.classList.remove('is-active'));
      swatch.classList.add('is-active');
      // Update selected label
      const label = swatch.closest('.product-option')?.querySelector('.product-option__label span');
      if (label && swatch.title) label.textContent = swatch.title;
    });
  });

  // Size buttons
  $$('.size-btn').forEach(btn => {
    if (btn.classList.contains('size-btn--unavailable')) return;
    on(btn, 'click', () => {
      const grid = btn.closest('.size-grid');
      if (grid) $$('.size-btn', grid).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      // Update hidden input
      const variantInput = btn.closest('form')?.querySelector('[name="id"]');
      if (variantInput && btn.dataset.variantId) variantInput.value = btn.dataset.variantId;
    });
  });

  // Quantity
  $$('.qty-btn').forEach(btn => {
    on(btn, 'click', () => {
      const input = btn.parentElement.querySelector('.qty-input');
      if (!input) return;
      const val = parseInt(input.value) || 1;
      const action = btn.dataset.action;
      if (action === 'increase') input.value = val + 1;
      if (action === 'decrease' && val > 1) input.value = val - 1;
    });
  });
}

// ---- Accordion ----
function initAccordion() {
  $$('.accordion-trigger').forEach(trigger => {
    on(trigger, 'click', () => {
      const item = trigger.closest('.accordion-item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      // Close all in same group
      const group = item.closest('.product-accordion');
      if (group) $$('.accordion-item', group).forEach(i => i.classList.remove('is-open'));
      if (!isOpen) item.classList.add('is-open');
    });
  });
}

// ---- Collection Filters ----
function initCollectionFilters() {
  // Filter group toggles
  $$('.filter-group__title').forEach(title => {
    on(title, 'click', () => {
      const group = title.closest('.filter-group');
      if (group) group.classList.toggle('is-open');
    });
  });

  // Mobile filter panel
  const mobileFilterBtn = $('.mobile-filter-btn');
  const mobileFilterPanel = $('.mobile-filter-panel');
  const filterOverlay = $('.mobile-menu__overlay');

  if (mobileFilterBtn && mobileFilterPanel) {
    on(mobileFilterBtn, 'click', () => {
      mobileFilterPanel.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });
  }

  // Active filter removal
  $$('.active-filter').forEach(filter => {
    on(filter, 'click', () => filter.remove());
  });
}

// ---- Wishlist (localStorage) ----
const Wishlist = {
  key: 'venum-wishlist',
  getItems() { return JSON.parse(localStorage.getItem(this.key) || '[]'); },
  setItems(items) { localStorage.setItem(this.key, JSON.stringify(items)); },
  toggle(id) {
    const items = this.getItems();
    const idx = items.indexOf(id);
    if (idx > -1) items.splice(idx, 1);
    else items.push(id);
    this.setItems(items);
    return idx === -1;
  },
  has(id) { return this.getItems().includes(id); },

  init() {
    $$('.product-card__wishlist').forEach(btn => {
      const id = btn.dataset.productId;
      if (!id) return;
      if (this.has(id)) btn.classList.add('is-active');
      on(btn, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = this.toggle(id);
        btn.classList.toggle('is-active', added);
        showToast(added ? 'Added to wishlist' : 'Removed from wishlist');
      });
    });
  }
};

// ---- Counter Animation (Stats) ----
function initCounters() {
  const counters = $$('.stat-item__number[data-target]');
  if (!counters.length) return;

  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseInt(el.dataset.target) || 0;
      const duration = 2000;
      const start = performance.now();
      const suffix = el.dataset.suffix || '';
      const prefix = el.dataset.prefix || '';

      function tick(now) {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = prefix + Math.round(eased * target) + suffix;
        if (progress < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      obs.unobserve(el);
    });
  }, { threshold: 0.5 });
  counters.forEach(el => obs.observe(el));
}

// ---- Color swatch image swap (product cards) ----
function initCardColorSwatches() {
  $$('.product-card').forEach(card => {
    const swatches = $$('.color-swatch[data-img]', card);
    const img = card.querySelector('.product-card__img--main');
    if (!swatches.length || !img) return;

    swatches.forEach(swatch => {
      on(swatch, 'mouseenter', () => {
        swatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        if (swatch.dataset.img) img.src = swatch.dataset.img;
      });
    });
  });
}

// ---- Sticky Header behavior ----
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;

  let lastScroll = 0;
  on(window, 'scroll', debounce(() => {
    const scroll = window.scrollY;
    if (scroll > 80 && scroll > lastScroll) {
      header.style.transform = 'translateY(-100%)';
    } else {
      header.style.transform = 'translateY(0)';
    }
    lastScroll = scroll;
  }, 50));
}

// ---- Product image zoom on hover ----
function initImageZoom() {
  $$('.product-gallery__main').forEach(wrap => {
    on(wrap, 'mousemove', (e) => {
      const img = wrap.querySelector('img');
      if (!img) return;
      const rect = wrap.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
      img.style.transform = 'scale(1.5)';
    });
    on(wrap, 'mouseleave', () => {
      const img = wrap.querySelector('img');
      if (img) img.style.transform = 'scale(1)';
    });
  });
}

// ---- Announcement bar marquee ----
function initMarquee() {
  const marquee = $('.announcement-bar__marquee');
  if (!marquee) return;
  // Simple CSS marquee via JS clone
  const content = marquee.innerHTML;
  marquee.innerHTML = content + content;
}

// ---- Initialize Everything ----
document.addEventListener('DOMContentLoaded', () => {
  initLazyLoad();
  initFadeIn();
  initAnnouncementBar();
  initMobileMenu();
  initSearch();
  initHeroSlider();
  initProductCarousels();
  CartDrawer.init();
  initAddToCart();
  initProductGallery();
  initProductOptions();
  initAccordion();
  initCollectionFilters();
  Wishlist.init();
  initCounters();
  initCardColorSwatches();
  initStickyHeader();
  initImageZoom();
  initMarquee();
});

// Re-init after Shopify sections are reloaded (Theme Editor)
document.addEventListener('shopify:section:load', () => {
  initLazyLoad();
  initHeroSlider();
  initProductCarousels();
  initCounters();
});
